import FilterList from '@mui/icons-material/FilterList';
import { Grid, Menu, Typography } from "@mui/material";
import { withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import { DefaultListParameters } from "assets/appConfig";
import { TimelineStyleClasses } from "assets/jss/common-styles";
import ButtonResetFilter from "components/Buttons/ButtonResetFilter";
import IconButtonOrderBy from "components/Buttons/IconButtonOrderby";
import IconButtonSubTopic from "components/Buttons/IconButtonSubTopic";
import { LoadingIndicator } from "components/CallServer/LoadingIndicator";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import CountFilter from "components/CountFilter";
import { MuiFilterAppBar, MuiFilterToolbar } from "components/MuiExtras/Filter";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiClickable from "components/MuiExtras/MuiClickable";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiMenuItemChecked from "components/MuiExtras/MuiMenuItemChecked";
import MuiPagination from "components/MuiExtras/MuiPagination";
import MuiColor from "components/MuiExtras/Typography/MuiColor";
import { WithSnackbar, withSnackbar } from "components/SnackbarProvider/Snackbar";
import { GetKnowledgeTestTopicsOrderBy, GetKnowledgeTestTopicsParams, GetKnowledgeTestTopicsParamsFilter, KnowledgeTestTopicItemDto, PagedResult, TopicSuggestionDto } from "models/ApiModels";
import { RequestLifeCycle } from "models/AppModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router";
import { RoutePaths } from "routes/RouteConfig";
import KnowledgeTestTopicCard from "views/KnowledgeTest/Components/KnowledgeTestTopicCard";
import WhoCanAnswerKnowledgeTestDrawer from "views/KnowledgeTest/Dialogs/WhoCanAnswerKnowledgeTestDrawer";
import { KnowledgeTestAdminListParameters } from "views/KnowledgeTest/KnowledgeTestAdminList";
import TopicAutoComplete from "views/Topic/Components/TopicAutoComplete";
import TopicDashboardDialog from "views/Topic/Dialogs/TopicDashboardDialog";
import TopicRequiredMembersDialog from "views/Topic/Dialogs/TopicRequiredMembersDialog";
import CreateTest from "./CreateTest";

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IDataState {
    filter: GetKnowledgeTestTopicsParamsFilter;
    selectedTopic: TopicSuggestionDto[];
    orderBy: GetKnowledgeTestTopicsOrderBy;
    offset: number;
}

interface IState extends IDataState {
    addByMenuAnchorEl: any;
    dialog: any;
    lastError: any;
    list: PagedResult<KnowledgeTestTopicItemDto>;
    listCycle: RequestLifeCycle;
    orderByMenuAnchorEl: any;
}

interface IProps {
}

export interface KnowledgeTestTopicListParameters {
    topic?: AutoCompleteDataModel;
}

type Props = IProps & WithStyles<typeof TimelineStyleClasses> & RouteComponentProps<any, any, any> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////////////////

class KnowledgeTestTopicList extends React.Component<Props, IState> {

    constructor(props: Props) {
        super(props);

        var oldState: IDataState = props.history.location.state || {};

        this.state =
        {
            ...{
                selectedTopic: null,
                filter: this.resetFilter(),
                orderBy: GetKnowledgeTestTopicsOrderBy.Name,

                list: { items: null, total: 0 },
                offset: 0,
                listCycle: RequestLifeCycle.None,
                lastError: null,

                dialog: null,
                addByMenuAnchorEl: null,
                orderByMenuAnchorEl: null,
            },
            ...oldState
        };
    }

    ///
    private res(r: string, f: any = null) {
        return this.props.t("Views.KnowledgeTest.TopicList." + r, f);
    }

    ///
    private updateHistory() {
        this.props.history.replace(this.props.location.pathname,
            {
                filter: this.state.filter,
                offset: this.state.offset,
                selectedTopic: this.state.selectedTopic
            } as IDataState
        );
    }

    ///
    private loadData(offset: number) {

        const p: GetKnowledgeTestTopicsParams = {
            filter: this.state.filter,
            orderBy: this.state.orderBy,
            range: { offset: offset, take: DefaultListParameters.viewRecordsByPage }
        };

        this.setState({ listCycle: RequestLifeCycle.Loading, orderByMenuAnchorEl: null, dialog: null });

        api.getKnowledgeTestTopics(p)
            .then((result) => {
                this.setState({ list: result, listCycle: RequestLifeCycle.None, offset: offset }, () => this.updateHistory());
            })
            .catch((error) => {
                this.setState({ list: null, lastError: error, listCycle: RequestLifeCycle.LoadFailed, offset: 0 });
            });
    }

    ///
    private buildOrderByMenu() {
        const { orderBy } = this.state;
        return (
            <>
                <IconButtonOrderBy
                    color="inherit"
                    onClick={
                        (e) => {
                            this.setState({ orderByMenuAnchorEl: e.currentTarget });
                        }
                    }
                />
                <Menu
                    anchorEl={this.state.orderByMenuAnchorEl}
                    open={this.state.orderByMenuAnchorEl != null}
                    onClose={
                        () => {
                            this.setState({ orderByMenuAnchorEl: null });
                        }}
                >
                    <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.Name")} value={GetKnowledgeTestTopicsOrderBy.Name} currentSelection={orderBy} onClick={this.applyOrder} />
                    <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.NumberOfTests")} value={GetKnowledgeTestTopicsOrderBy.NumberOfTests} currentSelection={orderBy} onClick={this.applyOrder} />
                </Menu>
            </>
        );
    }

    ///
    private handleEdit = (item: KnowledgeTestTopicItemDto) => {
        this.props.history.push(RoutePaths.KnowledgeTestAdminList,
            {
                topic: {
                    key: item.topicId,
                    label: item.name,
                    thumbnailUrl: item.thumbnailUrl,
                },
            } as KnowledgeTestAdminListParameters);
    };

    ///
    private handleNewTest = (item: KnowledgeTestTopicItemDto) => {
        this.setState({
            dialog: (
                <CreateTest
                    initialTopic={
                        {
                            key: item.topicId,
                            label: item.name,
                            thumbnailUrl: item.thumbnailUrl,
                            canDelete: false
                        }
                    }
                    onClose={
                        (reload) =>
                            this.setState({ dialog: null },
                                () => {
                                    if (reload) {
                                        this.props.snackbar.showMessage(this.props.t("Views.KnowledgeTest.CreateTest.SaveSuccess"), "success");
                                        this.loadData(this.state.offset);
                                    }
                                })
                    }
                />
            )
        });
    };

    ///
    private handleRequiredMembers = (topic: KnowledgeTestTopicItemDto) => {
        this.setState({
            dialog: (
                <TopicRequiredMembersDialog
                    topic={topic}
                    onClose={() => this.setState({ dialog: null }, () => this.loadData(this.state.offset))}
                />
            )
        });
    };

    ///
    private handleViewMembers = (topic: KnowledgeTestTopicItemDto) => {
        this.setState({
            dialog: (
                <WhoCanAnswerKnowledgeTestDrawer
                    topicId={topic.topicId}
                    onClose={() => this.setState({ dialog: null })}
                />
            )
        });
    };

    ///
    private handleDashboard(topic: KnowledgeTestTopicItemDto) {
        this.setState({
            dialog: (
                <TopicDashboardDialog
                    topic={topic}
                    onClose={() => this.setState({ dialog: null })}
                />
            )
        });
    }

    ///
    private handleFilterSwitch = () => {
        this.setState((state) => {
            state.filter.onlyWithTests = !state.filter.onlyWithTests;
            return state;
        }, () => this.loadData(0)
        );

    };

    ///
    private handleShowSubTopics = () => {
        this.setState((state) => {
            state.filter.showSubtopics = !state.filter.showSubtopics;
            return state;
        }, () => this.loadData(0));
    };

    ///
    private handleResetAndReload() {
        this.setState(
            { filter: this.resetFilter(), selectedTopic: null },
            () => this.loadData(0)
        );
    }

    ///
    private resetFilter(): GetKnowledgeTestTopicsParamsFilter {
        return {
            topicId: null,
            showSubtopics: false,
            onlyWithTests: false,
        };
    }

    ///
    private isFiltered() {
        const { filter } = this.state;
        return filter.topicId != null || filter.showSubtopics != false || filter.onlyWithTests != false;
    }

    ///
    private applyOrder = (orderBy: GetKnowledgeTestTopicsOrderBy) => {
        this.setState(
            { orderBy },
            () => this.loadData(0)
        );
    };

    ///
    private renderAdvFilter(): JSX.Element {
        const { selectedTopic, filter } = this.state;

        return (
            <MuiFilterAppBar position="static">
                <MuiFilterToolbar disableGutters={false}>


                    <Grid container spacing={1} justifyContent="space-between" alignItems="center" >
                        <Grid item>
                            <MuiClickable
                                onClick={this.handleFilterSwitch}
                            >
                                <MuiColor variant={filter.onlyWithTests ? "warning" : "inherit"}>
                                    <Grid container alignItems="center">
                                        <Grid item>
                                            <FilterList />&nbsp;
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="button">{this.res("ListTestsWithTopicsOnlyText")}</Typography>
                                        </Grid>
                                    </Grid>
                                </MuiColor>
                            </MuiClickable>
                        </Grid>
                        <Grid item>
                            {
                                this.state.selectedTopic &&
                                <IconButtonSubTopic
                                    color={filter.showSubtopics ? "warning" : "inherit"}
                                    onClick={this.handleShowSubTopics}
                                />
                            }
                            {this.buildOrderByMenu()}
                        </Grid>
                    </Grid>

                    <TopicAutoComplete
                        id="topicFilter"
                        placeholder={this.res("TopicPlaceholder")}
                        margin="dense"
                        multiple={false}
                        showAvatar={false}
                        selectedItems={selectedTopic}
                        chipColor="info"
                        onChange={
                            (items) => {
                                this.setState((state) => {
                                    state.filter.topicId = items.length == 0 ? null : items[0].key;
                                    return {
                                        ...state,
                                        selectedTopic: items.length == 0 ? null : items,
                                    };
                                }, () => this.loadData(0));
                            }
                        }
                    />

                </MuiFilterToolbar>
            </MuiFilterAppBar>
        );
    }

    ///
    private renderItems() {
        const { list } = this.state;

        if (list.total == 0 && !this.isFiltered())
            return <MuiMessageBox variant="info" message={this.res("FilteredResultText_zero")} />;

        if (list.total == 0 && this.isFiltered())
            return (
                <MuiMessageBox
                    variant="warning"
                    action={<ButtonResetFilter color="warning" onClick={() => this.handleResetAndReload()} />}
                    message={this.res("ResultText_zero")}
                />
            );


        return (
            <MuiContainer margin="normal">
                <MuiContainer margin="normal">
                    <CountFilter
                        count={list.total}
                        filtered={this.isFiltered()}
                        resource="Views.KnowledgeTest.TopicList.ResultText"
                        filteredResource="Views.KnowledgeTest.TopicList.FilteredResultText"
                        onResetFilter={() => this.handleResetAndReload()}
                    />
                </MuiContainer>
                {
                    list.items.map((item, index) => (
                        <MuiContainer margin="dense" key={index}>
                            <KnowledgeTestTopicCard
                                item={item}
                                onEdit={() => this.handleEdit(item)}
                                onNewTest={() => this.handleNewTest(item)}
                                onRequiredMembers={() => this.handleRequiredMembers(item)}
                                onViewMembers={() => this.handleViewMembers(item)}
                                onDashboard={() => this.handleDashboard(item)}
                            />
                        </MuiContainer>
                    ))
                }
                <MuiPagination
                    limit={DefaultListParameters.viewRecordsByPage}
                    offset={this.state.offset}
                    total={this.state.list.total}
                    onClick={(e, offset) => this.loadData(offset)}
                />
            </MuiContainer>
        );
    }

    ///
    private renderList() {
        const { lastError, listCycle } = this.state;

        if (lastError)
            return <RequestErrorReport error={lastError} />;

        return (
            <>
                {this.state.dialog}

                <MuiContainer margin="normal">
                    {this.renderAdvFilter()}
                </MuiContainer>

                {
                    listCycle == RequestLifeCycle.Loading
                        ? <LoadingIndicator />
                        : this.renderItems()
                }
            </>
        );
    }

    ///
    public componentDidMount() {
        this.loadData(this.state.offset);
    }

    ///
    public render() {
        return (
            <div className={this.props.classes.timelineContainer}>
                {this.renderList()}
            </div>
        );
    }
}

export default withSnackbar(withTranslation()(withStyles(TimelineStyleClasses)(KnowledgeTestTopicList)));